/**
 * Client-access registry — single source of truth for per-CLIENT access.
 *
 * Spec: 05-client-access-registry (spec-01). This increment centralizes two
 * dimensions for every client: allowed_use_cases and vitamin_populations. The
 * per-client constants that used to live in LandingScreen.tsx / UseCaseSelector.tsx
 * / VitaminConceptTestForm.tsx are replaced by lookups here.
 *
 * Matcher note: clients historically matched users in three different ways —
 * a VITE_*_ALLOWED_DOMAINS env var, hardcoded domain suffixes, or a hardcoded
 * email allow-list. The registry carries all three so each client resolves
 * EXACTLY as before; `resolveClientKey` applies them in that order.
 *
 * Admin bypass is NOT modelled here — callers keep their own `isAdmin` handling,
 * exactly as today. Numeric segment/survey IDs stay in segments.ts.
 */
import registry from './clients.json';

export type UseCaseType = string;

export interface ClientPopulation {
  name: string;
  displayName?: string;
}

interface ClientMatch {
  envDomainsVar?: string;
  envDomainsFallback?: string;
  domains?: string[];
  emails?: string[];
}

/** A chat segment option: references a SEGMENT_IDS key (resolved in segments.ts), carries its
 *  own display label (the same id shows different labels across client lists). */
export interface ChatSegmentOption {
  id: string;
  label: string;
  admin_only?: boolean;
}

/** Per-surface chat-segment config for a client. `segment_list: null` = this client does not
 *  override the surface's default list (it gets a single auto-selected default_segment). */
export interface ChatSurfaceConfig {
  default_segment: string;
  dropdown_visible: boolean;
  segment_list: ChatSegmentOption[] | null;
}

type ChatSurface = 'ask_natural_organic';

interface ClientEntry {
  match: ClientMatch;
  allowed_use_cases: UseCaseType[];
  vitamin_populations: ClientPopulation[];
  chat?: Partial<Record<ChatSurface, ChatSurfaceConfig>>;
}

const CLIENTS = (registry as { clients: Record<string, ClientEntry> }).clients;

/** All client keys, in registry declaration order (used by the parity test). */
export const CLIENT_KEYS = Object.keys(CLIENTS);

const parseEnvDomains = (raw: unknown): string[] =>
  String(raw ?? '')
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);

/** Resolve the full domain allow-list for a client (env var merged with fallback + literals). */
const domainsFor = (m: ClientMatch): string[] => {
  const out = new Set<string>((m.domains ?? []).map((d) => d.trim().toLowerCase()));
  if (m.envDomainsVar) {
    const envRaw = (import.meta.env as Record<string, string | undefined>)[m.envDomainsVar];
    const parsed = parseEnvDomains(envRaw);
    // Preserve the old `|| 'amys.com'`-style fallback: only when the env var is unset/blank.
    (parsed.length ? parsed : parseEnvDomains(m.envDomainsFallback)).forEach((d) => out.add(d));
  }
  return [...out];
};

const emailMatches = (email: string, entry: ClientEntry): boolean => {
  const lower = email.toLowerCase();
  const domain = lower.split('@')[1] || '';
  if ((entry.match.emails ?? []).some((e) => e.toLowerCase() === lower)) return true;
  return domainsFor(entry.match).some((d) =>
    // Domain entries may be a bare domain ("amys.com") or a suffix; match either "@d" or the domain part.
    lower.endsWith(`@${d}`) || domain === d,
  );
};

/** The client key a user belongs to, or null. First match in registry order wins. */
export function resolveClientKey(email: string | null | undefined): string | null {
  if (!email) return null;
  for (const key of CLIENT_KEYS) {
    if (emailMatches(email, CLIENTS[key])) return key;
  }
  return null;
}

export function allowedUseCasesFor(clientKey: string | null): UseCaseType[] | null {
  return clientKey ? CLIENTS[clientKey].allowed_use_cases : null;
}

export function vitaminPopulationsFor(clientKey: string | null): ClientPopulation[] | null {
  return clientKey ? CLIENTS[clientKey].vitamin_populations : null;
}

/** Raw entry access for the parity snapshot test. */
export function clientEntry(clientKey: string): ClientEntry {
  return CLIENTS[clientKey];
}

/** Chat-segment config for a client on a given surface, or null if the client has none
 *  (admin / non-client / a client this surface doesn't special-case). */
export function chatSurfaceConfigFor(
  clientKey: string | null,
  surface: ChatSurface,
): ChatSurfaceConfig | null {
  if (!clientKey) return null;
  return CLIENTS[clientKey].chat?.[surface] ?? null;
}
